import { useState } from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './index.scss';

/* A name box that sits above a table. The paged screens hand `onSearch` a
   refetch; the cart, which holds every row already, filters in place. */
const TableSearch = ({ defaultValue = '', onSearch, className = '', placeholder, minLength }) => {
  const { t } = useTranslation('common');
  const [error, setError] = useState('');
  /* Controlled so the text on screen always matches the term that was searched.
     It was uncontrolled before, which let a trimmed search desync from the box
     on the screens that do not remount it. */
  const [value, setValue] = useState(defaultValue);

  /* antd fires this for both the Search button and the clear icon - the icon's
     event is a synthetic click carrying the already-emptied value (see
     Input.Search's onChange). Take the value from antd rather than from state,
     which on the clear path still holds the term being cleared. */
  const handleSearch = (searchValue) => {
    const term = (searchValue ?? '').trim();
    // Whitespace-only is an empty search, not a short one - clear the filter.
    if (term && minLength && term.length < minLength) {
      setError(t('search-min-length', { count: minLength }));
      return;
    }
    setError('');
    setValue(term);
    onSearch(term);
  };

  return (
    <div className={`table-search ${className}`.trim()}>
      <Input.Search
        value={value}
        placeholder={placeholder || t('search-by-name')}
        onSearch={handleSearch}
        /* Typing only updates the box, with one exception: backspacing the last
           character clears the filter, otherwise the table stays filtered by a
           term that is no longer on screen - most obvious on the cart, which
           filters in place as you type rather than on submit.

           A box with a minimum length is the exception. That is the code
           listing, where searching on empty drops the client out of code mode
           mid-edit; there the clear icon is what clears the filter, and antd
           routes that through onSearch above. */
        onChange={(event) => {
          const next = event.target.value;
          setValue(next);
          if (error) setError('');
          if (!minLength && !next.trim() && value) onSearch('');
        }}
        prefix={<SearchOutlined className="table-search__icon" />}
        aria-label={placeholder || t('search-by-name')}
        allowClear
        enterButton={t('search')}
        size="large"
      />
      {error && <div className="table-search__error">{error}</div>}
    </div>
  );
};

TableSearch.propTypes = {
  defaultValue: PropTypes.string,
  onSearch: PropTypes.func.isRequired,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  minLength: PropTypes.number,
};

export default TableSearch;
